<template>
  <nav class="navbar" role="navigation" aria-label="Main navigation">
    <a class="skip-link" href="#main-content">{{ $t('common.skipToContent') }}</a>

    <div class="navbar-container">
      <div class="navbar-brand">
        <router-link :to="{ name: 'home' }" class="brand-link" @click="closeMenu">
          {{ siteName }}
        </router-link>
      </div>

      <div class="desktop-menu">
        <router-link
          v-for="item in menuItems"
          :key="item.routeName"
          :to="{ name: item.routeName }"
          class="nav-link"
          active-class="active"
        >
          {{ $t(item.titleKey) }}
        </router-link>

        <!-- Language Switcher Desktop -->
        <div class="language-switcher">
          <button
            class="lang-btn"
            :class="{ active: currentLocale === 'es' }"
            @click="changeLanguage('es')"
          >
            ES
          </button>
          <div class="switcher-dot"></div>
          <button
            class="lang-btn"
            :class="{ active: currentLocale === 'en' }"
            @click="changeLanguage('en')"
          >
            EN
          </button>
        </div>

        <router-link
          :to="{ name: 'login' }"
          class="login-icon-link"
          :title="$t('website.menu.professionalAccess')"
        >
          <i class="fas fa-user-circle"></i>
        </router-link>
      </div>

      <!-- Mobile Toggle -->
      <button
        class="navbar-toggle"
        :class="{ 'is-open': isOpen }"
        :aria-expanded="isOpen"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <i :class="isOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
      </button>
    </div>

    <!-- Mobile Menu Drawer -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isOpen" class="mobile-backdrop" @click="closeMenu"></div>
      </Transition>

      <Transition name="slide">
        <div v-if="isOpen" class="mobile-drawer" role="dialog" aria-modal="true">
          <div class="drawer-header">
            <span class="drawer-title">{{ $t('website.menu.title') }}</span>
            <button class="close-btn" aria-label="Close menu" @click="closeMenu">&times;</button>
          </div>
          <nav class="drawer-nav">
            <router-link
              v-for="item in menuItems"
              :key="item.routeName"
              :to="{ name: item.routeName }"
              class="drawer-link"
              active-class="active"
              @click="closeMenu"
            >
              {{ $t(item.titleKey) }}
            </router-link>

            <router-link :to="{ name: 'login' }" class="drawer-login-link" @click="closeMenu">
              <i class="fas fa-user-circle"></i> {{ $t('website.menu.professionalAccess') }}
            </router-link>

            <!-- Language Switcher Mobile -->
            <div class="drawer-language-switcher">
              <span class="lang-label">{{ $t('website.menu.language') }}</span>
              <div class="lang-options">
                <button
                  class="drawer-lang-btn"
                  :class="{ active: currentLocale === 'es' }"
                  @click="changeLanguage('es')"
                >
                  Español
                </button>
                <button
                  class="drawer-lang-btn"
                  :class="{ active: currentLocale === 'en' }"
                  @click="changeLanguage('en')"
                >
                  English
                </button>
              </div>
            </div>
          </nav>
        </div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const route = useRoute();
const isOpen = ref(false);

const currentLocale = computed(() => locale.value);

const changeLanguage = (lang: string) => {
  locale.value = lang;
  localStorage.setItem('user-locale', lang);
};

const menuItems = [
  { titleKey: 'menu.book', routeName: 'index' },
  { titleKey: 'menu.about', routeName: 'about' },
  { titleKey: 'menu.specialties', routeName: 'specialties' },
];

const toggleMenu = () => (isOpen.value = !isOpen.value);
const closeMenu = () => (isOpen.value = false);
const siteName = import.meta.env.VITE_SITE_NAME;

watch(() => route.path, closeMenu);

watch(isOpen, (val) => {
  if (val) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) closeMenu();
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<style scoped>
.navbar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  height: 70px;
  box-shadow: 0 4px 20px rgba(141, 23, 196, 0.08);
  position: sticky;
  top: 0;
  z-index: 2000;
  padding: 0 1.5rem;
  border-bottom: 1px solid rgba(141, 23, 196, 0.1);
  transition: all 0.3s ease;
}

:global(.dark-mode) .navbar {
  background: rgba(15, 23, 42, 0.85);
  border-bottom-color: rgba(141, 23, 196, 0.2);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.navbar-container {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-link {
  font-size: 1.6rem;
  font-weight: 950;
  background: linear-gradient(135deg, #8d17c4 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-decoration: none;
  letter-spacing: -0.04em;
  transition: transform 0.2s ease;
}

.brand-link:hover {
  transform: scale(1.02);
}

/* Desktop Menu */
.desktop-menu {
  display: none;
  align-items: center;
  gap: 2.2rem;
}

.nav-link {
  color: #64748b;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 0;
}

:global(.dark-mode) .nav-link {
  color: #94a3b8;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #8d17c4;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.nav-link:hover {
  color: #8d17c4;
}

.nav-link:hover::after,
.nav-link.active::after {
  width: 100%;
}

.nav-link.active {
  color: #8d17c4;
}

:global(.dark-mode) .nav-link.active,
:global(.dark-mode) .nav-link:hover {
  color: #c084fc;
}

/* Login Icon Navbar */
.login-icon-link {
  color: #8d17c4;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  padding: 5px;
}

.login-icon-link:hover {
  transform: scale(1.1);
  color: #a855f7;
}

:global(.dark-mode) .login-icon-link {
  color: #c084fc;
}

/* Mobile Toggle */
.navbar-toggle {
  background: white;
  border: 1px solid rgba(141, 23, 196, 0.15);
  border-radius: 12px;
  cursor: pointer;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  color: #8d17c4;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(141, 23, 196, 0.08);
}

.navbar-toggle:hover {
  transform: translateY(-2px);
  border-color: #8d17c4;
  box-shadow: 0 6px 16px rgba(141, 23, 196, 0.12);
}

.navbar-toggle.is-open {
  background: #fdf4ff;
  color: #8d17c4;
  transform: rotate(90deg);
}

:global(.dark-mode) .navbar-toggle {
  background: #1e293b;
  border-color: rgba(192, 132, 252, 0.2);
  color: #c084fc;
}

/* Transitions */
.mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 5, 25, 0.4);
  z-index: 9998;
  backdrop-filter: blur(8px);
}

.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  background: #ffffff;
  z-index: 9999;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  padding: 2rem 1.5rem;
}

:global(.dark-mode) .mobile-drawer {
  background: #1e293b;
}

.drawer-header {
  margin-bottom: 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-title {
  font-weight: 800;
  font-size: 1.4rem;
  color: #8d17c4;
}

.close-btn {
  background: rgba(141, 23, 196, 0.05);
  border: none;
  font-size: 1.8rem;
  color: #8d17c4;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.drawer-link {
  padding: 1rem 1.25rem;
  text-decoration: none;
  color: #64748b;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 16px;
  transition: all 0.2s ease;
  background: #f8fafc;
}

:global(.dark-mode) .drawer-link {
  background: #334155;
}

.drawer-link.active {
  background: #fdf4ff;
  color: #8d17c4;
}

.drawer-login-link {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 1rem;
  color: #8d17c4;
  font-weight: 800;
  font-size: 1.2rem;
  text-decoration: none;
  border: 2px solid rgba(141, 23, 196, 0.2);
  border-radius: 18px;
  transition: all 0.2s ease;
}

.drawer-login-link i {
  font-size: 1.5rem;
}

:global(.dark-mode) .drawer-login-link {
  color: #c084fc;
  border-color: rgba(192, 132, 252, 0.2);
}

.drawer-language-switcher {
  margin-top: auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(141, 23, 196, 0.1);
}

.lang-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 1rem;
}

.lang-options {
  display: flex;
  gap: 10px;
}

.drawer-lang-btn {
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  background: #f1f5f9;
  border: 1px solid transparent;
  color: #64748b;
  font-weight: 700;
  cursor: pointer;
}

:global(.dark-mode) .drawer-lang-btn {
  background: #334155;
}

.drawer-lang-btn.active {
  background: #fdf4ff;
  border-color: #8d17c4;
  color: #8d17c4;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

@media (min-width: 768px) {
  .desktop-menu {
    display: flex;
  }
  .navbar-toggle {
    display: none;
  }
}

.language-switcher {
  display: flex;
  align-items: center;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  margin-left: 0.5rem;
}

:global(.dark-mode) .language-switcher {
  background: #334155;
}

.lang-btn {
  background: none;
  border: none;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  padding: 6px 10px;
  font-size: 0.75rem;
}

.lang-btn.active {
  background: white;
  color: #8d17c4;
}

.switcher-dot {
  width: 4px;
  height: 4px;
  background: #cbd5e1;
  border-radius: 50%;
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
